import { Skeleton } from '@shared/ui/skeleton/skeleton.component';

import backlogStyles from '../backlog/backlog.page.module.css';
import boardViewStyles from '../board/board-view.component.module.css';
import columnStyles from '../board/column.component.module.css';
import switcherStyles from '../board/status-switcher.component.module.css';
import cardStyles from '../board/task-card.component.module.css';
import taskListStyles from '../board/task-list.component.module.css';
import rowStyles from '../board/task-row.component.module.css';
import epicBoardStyles from '../epics/epic-board.component.module.css';
import epicCardStyles from '../epics/epic-card.component.module.css';
import epicColumnStyles from '../epics/epic-column.component.module.css';
import epicDetailStyles from '../epics/epic-detail.component.module.css';
import epicHeaderStyles from '../epics/epic-detail-header.component.module.css';
import epicsOverviewStyles from '../epics/epics-overview.component.module.css';
import liveStyles from '../live/live.page.module.css';
import toolbarStyles from '../search/filter-toolbar.component.module.css';
import boardHeaderStyles from './board-header.component.module.css';
import styles from './skeletons.component.module.css';

// Loading placeholders that reuse the REAL components' CSS (board-header, the command
// bar, task-row, task-card, column, and each view's container) so every box, padding,
// gap and flex is identical to the populated UI by construction — content just swaps
// in, no shift. Leaf bars are sized to the measured real elements. Each view (board /
// backlog / live) has its own full skeleton — the command band over its own content
// shape — matching what it eventually renders.

const PILL = { borderRadius: 'var(--radius-full)' };
const R_MD = { borderRadius: 'var(--radius-md)' };

// Title / eyebrow bars sit inside the real .title / .eyebrow elements so they
// inherit the exact font size + line height (and its mobile step-down); the line
// box keeps its height, so the text block — and the header's height — stays identical
// to the populated header while the bar itself reads as text.
const INLINE_BAR = { display: 'inline-block', verticalAlign: 'middle', marginBlock: 0 };
// The column's quick-add field: a control-height box with the same spacing-2 tail the
// real .quickAdd carries, so the first card lands at the same offset under the header.
const QUICK_ADD = { borderRadius: 'var(--radius-md)', marginBlockEnd: 'var(--spacing-2)' };
// The Done column leads with the left-aligned "Archive all Done" action instead.
const ARCHIVE = {
	alignSelf: 'flex-start',
	borderRadius: 'var(--radius-md)',
	marginBlockEnd: 'var(--spacing-2)',
};
// The epic detail header's progress bar: fills the row (flex 1) at the real 0.5rem height.
const PROGRESS = { flex: 1, blockSize: '0.5rem', borderRadius: 'var(--radius-full)' };
// The epic card's colour rail (absolute, down the leading edge). Rendered as a plain
// Skeleton so it's the same shimmer as every other bar, not the epic's own colour.
const RAIL = {
	position: 'absolute',
	insetBlock: 0,
	insetInlineStart: 0,
	inlineSize: '3px',
	borderRadius: 0,
} as const;

/* ---------- headers ---------- */

/** The sticky command band (BoardHeader) while it loads. Mirrors BoardHeader's DOM
 *  and reuses its CSS + the command bar's, so every padding, gap, the tier-2 collapse
 *  and the mobile rules match by construction. The leaf bars are sized to the measured
 *  real elements: the eyebrow / title sit inside the real .eyebrow / .title so the
 *  text block (and thus the header height) is exact on both breakpoints; the New
 *  action, search, and Filters + Views get their measured widths — and the phone
 *  step-down to icon-only + a filling search — from this module's CSS. `action` is the
 *  primary-button slot (off on the Archive view, which has none). */
export function BoardHeaderSkeleton({
	titleWidth,
	action = false,
	controls = 'board',
}: {
	titleWidth: number;
	action?: boolean;
	/** Which tier-2 controls sit at the trailing end: the board's Filters + Views, or
	 *  the epics view's owner filter + Show-archived toggle (wider labels). */
	controls?: 'board' | 'epics';
}) {
	const [leadingControl, trailingControl] =
		controls === 'epics'
			? [styles.headerOwner, styles.headerArchive]
			: [styles.headerFilters, styles.headerViews];
	return (
		<header className={boardHeaderStyles.header}>
			<div className={boardHeaderStyles.band}>
				<div className={boardHeaderStyles.tier1}>
					<div className={boardHeaderStyles.identity}>
						<div className={boardHeaderStyles.text}>
							{/* Project eyebrow: its colour dot + the project name, above the view title. */}
							<span className={boardHeaderStyles.eyebrow}>
								<Skeleton variant="circle" width={8} height={8} />
								<Skeleton width={68} height="0.72em" style={INLINE_BAR} />
							</span>
							{/* The view title with its count pill alongside. */}
							<div className={boardHeaderStyles.titleRow}>
								<h1 className={boardHeaderStyles.title}>
									<Skeleton width={titleWidth} height="0.82em" style={INLINE_BAR} />
								</h1>
								<Skeleton variant="block" width={30} height={22} style={PILL} />
							</div>
						</div>
					</div>
					{action ? (
						<div className={boardHeaderStyles.action}>
							<Skeleton variant="block" className={styles.headerAction} height={32} style={R_MD} />
						</div>
					) : null}
				</div>
				<div className={boardHeaderStyles.tier2wrap}>
					<div className={boardHeaderStyles.tier2}>
						<div className={boardHeaderStyles.tier2inner}>
							<div className={toolbarStyles.cmdbar}>
								<Skeleton
									variant="block"
									className={styles.headerSearch}
									height={32}
									style={R_MD}
								/>
								<div className={toolbarStyles.controls}>
									<Skeleton variant="block" className={leadingControl} height={32} style={R_MD} />
									<Skeleton variant="block" className={trailingControl} height={32} style={R_MD} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</header>
	);
}

/* ---------- rows (backlog / live) ---------- */

function RowSkeleton({ seed }: { seed: number }) {
	// The list row is key + title + type badge + the (hover-only) ⋯ slot; labels,
	// assignee, due and priority only show when set, so the resting row is just these.
	// The title fills the row, so a short bar reads like a real (truncated) title.
	const titleWidth = `${30 + ((seed * 17) % 50)}%`;
	return (
		<div className={rowStyles.row}>
			<span className={styles.keySlot}>
				<Skeleton width={40} height={12} />
			</span>
			<span className={rowStyles.title}>
				<Skeleton width={titleWidth} height={14} />
			</span>
			{/* Empty labels slot — present in the real row (0-width when unlabelled), so its
			    two flex gaps still sit between the title and the badge. */}
			<div className={rowStyles.labels} />
			<Skeleton variant="block" width={60} height={20} style={PILL} />
			<span className={styles.menuSlot} />
		</div>
	);
}

/** Just the rows (backlog / live). `quickAdd` renders the backlog's leading add-task
 *  field (reusing the real slot, so it pins to the foot on phones); the Archive list
 *  has none. Uses the real task-list container so its spacing matches by construction. */
export function TaskListSkeleton({
	rows = 8,
	quickAdd = false,
}: {
	rows?: number;
	quickAdd?: boolean;
}) {
	return (
		<div className={taskListStyles.list} aria-hidden>
			{quickAdd ? (
				<div className={taskListStyles.quickAddSlot}>
					<Skeleton variant="block" width="100%" height={34} style={QUICK_ADD} />
				</div>
			) : null}
			{Array.from({ length: rows }, (_, row) => (
				<RowSkeleton key={row} seed={row} />
			))}
		</div>
	);
}

/* ---------- board cards + columns ---------- */

function CardSkeleton({ seed }: { seed: number }) {
	// The resting card is the header (key + type badge) and the title; labels, assignee
	// and the epic chip only render when set, so the bare card is just these. The title
	// bar sits in the real .title so it keeps its line height. Reuses the real card CSS.
	const titleWidth = `${52 + ((seed * 13) % 34)}%`;
	return (
		<div className={cardStyles.card}>
			<header>
				<span className={cardStyles.key}>
					<Skeleton width={40} height={12} />
				</span>
				<span className={cardStyles.headerRight}>
					<Skeleton variant="block" width={60} height={20} style={PILL} />
				</span>
			</header>
			<div className={cardStyles.title}>
				<Skeleton width={titleWidth} height="0.9em" style={INLINE_BAR} />
			</div>
		</div>
	);
}

/** The phone-only column switcher (StatusSwitcher) — a row of status pills above the
 *  single visible column. Reuses the real switcher CSS; hidden on tablet/desktop, where
 *  the real board shows every column instead. */
function BoardSwitcherSkeleton({ tabs = [46, 78, 58, 40, 46] }: { tabs?: number[] }) {
	// Status tabs, the first active. Board: To do / In progress / Review / Test / Done;
	// Epics pass their own three (Planned / Active / Done).
	return (
		<div className={styles.switcherRow} aria-hidden>
			<div className={switcherStyles.switcher}>
				{tabs.map((width, tab) => (
					<span key={tab} className={switcherStyles.tab} data-active={tab === 0}>
						<Skeleton width={width} height={11} style={INLINE_BAR} />
						<span className={switcherStyles.count}>
							<Skeleton width={8} height={11} style={INLINE_BAR} />
						</span>
					</span>
				))}
			</div>
		</div>
	);
}

/** Just the columns (used inside the board view skeleton). Mirrors the real board's
 *  five status columns, each with its leading quick-add (or, for Done, the archive
 *  action), plus the phone column switcher above them. */
export function BoardSkeleton() {
	const columns = [3, 2, 1, 2, 1];
	let seed = 0;
	return (
		<>
			<BoardSwitcherSkeleton />
			<div className={styles.board} aria-hidden>
				{columns.map((count, column) => {
					const isDone = column === columns.length - 1;
					return (
						<div key={column} className={columnStyles.column}>
							<header>
								<Skeleton width={56} height={11} style={INLINE_BAR} />
								{/* Inline bar inside the real .count pill so it keeps its 1.5rem line
								    height (a block child would collapse it, shrinking the header ~8px
								    and pulling the whole column up under the band). */}
								<span className={columnStyles.count}>
									<Skeleton width={10} height={11} style={INLINE_BAR} />
								</span>
							</header>
							{isDone ? (
								// Done leads with the left-aligned "Archive all Done" action.
								<Skeleton variant="block" width={158} height={32} style={ARCHIVE} />
							) : (
								// The column's leading quick-add row (control-height + its spacing-2
								// tail): without it the cards ride up under the header.
								<Skeleton variant="block" width="100%" height={34} style={QUICK_ADD} />
							)}
							<div className={columnStyles.stack}>
								{Array.from({ length: count }, (_, card) => (
									<CardSkeleton key={card} seed={seed++} />
								))}
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
}

/* ---------- per-view composite skeletons (header + content) ---------- */

/** Board view — the command band over the board lanes, in board-view's own .board
 *  container so the header's full-bleed + the column gap match. */
export function BoardViewSkeleton({ titleWidth = 52 }: { titleWidth?: number }) {
	return (
		<div className={boardViewStyles.board}>
			<BoardHeaderSkeleton titleWidth={titleWidth} action />
			<BoardSkeleton />
		</div>
	);
}

/** Backlog view — the command band over the row list (with the leading quick-add),
 *  in backlog.page's .backlog. No board columns or column switcher here. */
export function BacklogViewSkeleton() {
	return (
		<div className={backlogStyles.backlog}>
			<BoardHeaderSkeleton titleWidth={70} action />
			<TaskListSkeleton quickAdd />
		</div>
	);
}

/** Archive (Live) view — the command band with no primary action, over the row list. */
export function LiveViewSkeleton() {
	return (
		<div className={liveStyles.live}>
			<BoardHeaderSkeleton titleWidth={66} />
			<TaskListSkeleton rows={6} />
		</div>
	);
}

/** One epic card: the colour rail, the EP-n ref + ⋯ menu, then the colour dot + name
 *  (one or two lines). Progress + assignees only show when set, so the resting card is
 *  just these. Reuses the real card CSS so padding, radius and heights match. */
function EpicCardSkeleton({ twoLine = false }: { twoLine?: boolean }) {
	return (
		<div className={epicCardStyles.card}>
			<Skeleton variant="block" style={RAIL} />
			<div className={epicCardStyles.top}>
				<Skeleton width={34} height={12} />
				<span className={epicCardStyles.topRight}>
					{/* The ⋯ menu is a 2rem button; it (not the key) sets the top row's height. */}
					<span className={epicCardStyles.menu} style={{ blockSize: '2rem' }}>
						<Skeleton
							variant="block"
							width={16}
							height={16}
							style={{ borderRadius: 'var(--radius-sm)' }}
						/>
					</span>
				</span>
			</div>
			<h3 className={epicCardStyles.name}>
				<Skeleton variant="circle" width={9} height={9} style={{ flex: 'none' }} />
				<span className={epicCardStyles.nameText}>
					<Skeleton width={twoLine ? '94%' : '66%'} height="0.85em" style={INLINE_BAR} />
					{twoLine ? (
						<>
							<br />
							<Skeleton width="52%" height="0.85em" style={INLINE_BAR} />
						</>
					) : null}
				</span>
			</h3>
		</div>
	);
}

/** The Epics overview (a Planned / Active / Done kanban of epic cards) while it loads:
 *  the plain "Epics" title, the owner + show-archived filter row, and the three epic
 *  columns (Done leads with the archive action), plus the phone status switcher. */
export function EpicsOverviewSkeleton() {
	// Card name lines per column, a mix of one- and two-line to read like real epics.
	const columns = [
		[false, false, false],
		[true, true, false],
		[true, false],
	];
	return (
		<div className={epicsOverviewStyles.overview} aria-hidden>
			{/* Same command band as the board (dot + title + count + New epic action, over a
			    search + two controls), so the header height matches the real one and the
			    columns below don't shift on load. "Epics" is short, hence the narrow title. */}
			<BoardHeaderSkeleton titleWidth={48} action controls="epics" />
			<BoardSwitcherSkeleton tabs={[54, 44, 40]} />
			<div className={epicsOverviewStyles.columns}>
				{columns.map((cards, column) => {
					const isDone = column === columns.length - 1;
					return (
						<div key={column} className={epicColumnStyles.column}>
							<header>
								<Skeleton width={52} height={11} style={INLINE_BAR} />
								<span className={epicColumnStyles.count}>
									<Skeleton width={10} height={11} style={INLINE_BAR} />
								</span>
							</header>
							{isDone ? (
								<Skeleton variant="block" width={158} height={32} style={ARCHIVE} />
							) : (
								<Skeleton variant="block" width="100%" height={34} style={QUICK_ADD} />
							)}
							<div className={epicColumnStyles.stack}>
								{cards.map((twoLine, card) => (
									<EpicCardSkeleton key={card} twoLine={twoLine} />
								))}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

/** The single-epic header (EpicDetailHeader) while it loads: the breadcrumb, the status
 *  pill + owner/menu row, the name, description, and the progress bar + count. Reuses the
 *  real header CSS so its layout and height match; the name / description sit in the real
 *  elements so they keep their font size + line height. */
function EpicDetailHeaderSkeleton() {
	return (
		<header className={epicHeaderStyles.header}>
			<div className={epicHeaderStyles.breadcrumb}>
				<Skeleton
					variant="block"
					width={20}
					height={20}
					style={{ borderRadius: 'var(--radius-sm)' }}
				/>
				<nav className={epicHeaderStyles.crumbs}>
					<Skeleton width={36} height={11} style={INLINE_BAR} />
					<span className={epicHeaderStyles.sep} aria-hidden>
						/
					</span>
					<Skeleton width={28} height={11} style={INLINE_BAR} />
				</nav>
			</div>
			<div className={epicHeaderStyles.top}>
				<div className={epicHeaderStyles.eyebrow}>
					<Skeleton variant="circle" width={10} height={10} style={{ flex: 'none' }} />
					<Skeleton variant="block" width={82} height={22} style={PILL} />
				</div>
				<div className={epicHeaderStyles.people}>
					<Skeleton variant="block" width={92} height={28} style={PILL} />
					<Skeleton
						variant="block"
						width={24}
						height={24}
						style={{ borderRadius: 'var(--radius-sm)' }}
					/>
				</div>
			</div>
			<div className={epicHeaderStyles.name}>
				<Skeleton width={260} height="0.9em" style={INLINE_BAR} />
			</div>
			<div className={epicHeaderStyles.description}>
				<Skeleton width={180} height="0.9em" style={INLINE_BAR} />
			</div>
			<div className={epicHeaderStyles.progressRow}>
				<Skeleton variant="block" style={PROGRESS} />
				<Skeleton width={56} height={12} />
			</div>
		</header>
	);
}

/** The single-epic view (EpicDetail) while it loads: the header skeleton over the epic's
 *  board (the same BoardSkeleton EpicBoard itself falls back to while its board loads). */
export function EpicDetailSkeleton() {
	return (
		<div className={epicDetailStyles.detail}>
			<EpicDetailHeaderSkeleton />
			<div className={epicBoardStyles.board}>
				<BoardSkeleton />
			</div>
		</div>
	);
}

/** The task detail (drawer) while it streams in — gradient hero + property chips. */
export function TaskDetailSkeleton() {
	const chips = [86, 96, 74, 120, 62];
	return (
		<div className={styles.detail} aria-hidden>
			<div className={styles.detailHeader}>
				<Skeleton width={64} height={12} />
				<Skeleton width="72%" height={20} style={{ marginBlockStart: 10 }} />
			</div>
			<div className={styles.detailBody}>
				<div className={styles.chips}>
					{chips.map((width, chip) => (
						<Skeleton key={chip} variant="block" width={width} height={32} style={R_MD} />
					))}
				</div>
				<Skeleton width={90} height={11} style={{ marginBlockStart: 8 }} />
				<Skeleton width="100%" height={13} />
				<Skeleton width="97%" height={13} />
				<Skeleton width="88%" height={13} />
				<Skeleton width="64%" height={13} />
			</div>
		</div>
	);
}

/* ---------- boot shell (root HydrateFallback) ---------- */

/**
 * The whole shell, for the app's initial boot (root HydrateFallback) before the
 * client hydrates. MUST render identically on the server/build-time prerender and
 * the client — the prerendered index.html is a single static file served for every
 * URL, so this can't be path-aware (a `typeof window` branch would render one shell
 * on the server and another on the client → hydration mismatch).
 *
 * So it shows the board shell — command band over the board columns — which is the
 * app's default landing (`/` redirects to a board). A header alone over an empty body
 * reads as a broken page, not a loading one; the cards are what make it a skeleton.
 * On a direct hard-load of the backlog or Archive the route's own row skeleton takes
 * over the moment it mounts, so the board shape shows only for that first paint.
 */
export function ShellSkeleton() {
	return (
		<div className={styles.shell} aria-hidden>
			<aside className={styles.sidebar}>
				<div className={styles.brandRow}>
					<span className={styles.brandDot} />
					<Skeleton width={52} height={13} />
				</div>
				<Skeleton variant="block" width="100%" height={40} style={R_MD} />
				<div className={styles.nav}>
					{[0, 1, 2, 3].map((item) => (
						<div key={item} className={styles.navItem}>
							<Skeleton variant="block" width={18} height={18} />
							<Skeleton width={54 + ((item * 13) % 26)} height={13} />
						</div>
					))}
				</div>
				<div className={styles.spacer} />
				<div className={styles.navItem}>
					<Skeleton variant="block" width={18} height={18} />
					<Skeleton width={120} height={12} />
				</div>
				<Skeleton variant="block" width="100%" height={38} style={PILL} />
				<div className={styles.account}>
					<Skeleton variant="circle" width={28} height={28} />
					<Skeleton width={96} height={13} />
				</div>
			</aside>
			<main className={styles.shellMain}>
				<div className={boardViewStyles.board}>
					<BoardHeaderSkeleton titleWidth={52} action />
					<BoardSkeleton />
				</div>
			</main>
		</div>
	);
}
